import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import { FilterDescriptorEx } from '@ups/xplat/features';

/**
 * The filter that loads the rows of one group key. A date key matches by its day (as before: a key sent without a zone is
 * read as local time, so its day is the stored day) or by its exact instant (Bug 37761: a UTC key sent with its Z, whose
 * local day can be the day before or after the stored one). Any other key is a plain equality.
 *
 * The exact leg is `eq key` OR the millisecond window [key, key + 1 ms), every bound an exact UTC instant:
 * - the window: a JS Date holds milliseconds only, so a DATETIME2 value with sub-millisecond ticks (e.g. ...44.6673333, as
 *   EF writes it) never equals its own key (code-juraj, PR 7959 slot 2);
 * - the `eq`: EF types both bounds as the column's store type, and a DATETIME parameter rounds to .000/.003/.007, so
 *   key + 1 ms rounds back to key and the window is empty there (code-data, PR 7999 slot 1). Never widen to + 2 ms: on
 *   DATETIME2 that takes in the next millisecond's rows.
 */
export const groupKeyFilter = (
  field: string,
  value: unknown
): FilterDescriptor | CompositeFilterDescriptor =>
  value instanceof Date
    ? {
        logic: 'or',
        filters: [
          { field, operator: 'eq', value },
          {
            field,
            operator: 'eq',
            value,
            exactInstant: true,
          } as FilterDescriptorEx,
          {
            logic: 'and',
            filters: [
              {
                field,
                operator: 'gte',
                value,
                exactInstant: true,
              } as FilterDescriptorEx,
              {
                field,
                operator: 'lt',
                value: new Date(value.getTime() + 1),
                exactInstant: true,
              } as FilterDescriptorEx,
            ],
          },
        ],
      }
    : { field, operator: 'eq', value };
